import React, { useEffect, useRef, useState } from "react";
import "./ReportEmbedFrame.css";

const DEFAULT_SETTLE_MS = 2200;
const DEFAULT_REVEAL_BUFFER_MS = 450;
const OVERLAY_FADE_MS = 360;

export default function ReportEmbedFrame({
  src,
  title,
  loadingImage,
  variant = "inline",
  settleMs = DEFAULT_SETTLE_MS,
  onLoad,
  onError,
  sandbox = "allow-scripts allow-same-origin allow-popups allow-forms",
  allow = "fullscreen; clipboard-read; clipboard-write",
}) {
  const [iframeSrc, setIframeSrc] = useState("");
  const [iframeMounted, setIframeMounted] = useState(false);
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(Boolean(src));
  const [overlayFading, setOverlayFading] = useState(false);
  const [iframeRevealed, setIframeRevealed] = useState(false);
  const rafRef = useRef(null);
  const settleTimeoutRef = useRef(null);
  const revealTimeoutRef = useRef(null);
  const fadeTimeoutRef = useRef(null);

  useEffect(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }

    setIframeMounted(false);
    setEmbedLoaded(false);
    setOverlayVisible(Boolean(src));
    setOverlayFading(false);
    setIframeRevealed(false);
    setIframeSrc("");

    if (!src) return undefined;

    rafRef.current = window.requestAnimationFrame(() => {
      setIframeMounted(true);
      setIframeSrc(src);
      rafRef.current = null;
    });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
        settleTimeoutRef.current = null;
      }
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
    };
  }, [src]);

  const handleIframeLoad = (event) => {
    setEmbedLoaded(true);
    if (typeof onLoad === "function") {
      onLoad(event);
    }

    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
    }
    settleTimeoutRef.current = window.setTimeout(() => {
      settleTimeoutRef.current = null;

      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
      }

      revealTimeoutRef.current = window.setTimeout(() => {
        setIframeRevealed(true);
        setOverlayFading(true);
        revealTimeoutRef.current = null;

        if (fadeTimeoutRef.current) {
          clearTimeout(fadeTimeoutRef.current);
        }

        fadeTimeoutRef.current = window.setTimeout(() => {
          setOverlayVisible(false);
          setOverlayFading(false);
          fadeTimeoutRef.current = null;
        }, OVERLAY_FADE_MS);
      }, DEFAULT_REVEAL_BUFFER_MS);
    }, Math.max(settleMs, DEFAULT_SETTLE_MS));
  };

  const handleIframeError = (event) => {
    if (typeof onError === "function") {
      onError(event);
    }
  };

  return (
    <div
      className={`reports-embed-shell ${
        variant === "fullscreen"
          ? "reports-embed-shell--fullscreen"
          : "reports-embed-shell--inline"
      }`}
    >
      {iframeMounted && iframeSrc ? (
        <iframe
          src={iframeSrc}
          title={title}
          className={`reports-iframe ${
            iframeRevealed ? "is-revealed" : embedLoaded ? "is-loaded" : "is-loading"
          }`}
          sandbox={sandbox}
          allow={allow}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      ) : null}

      {overlayVisible ? (
        <div
          className={`reports-embed-overlay ${overlayFading ? "is-fading" : "is-visible"}`}
          role="status"
          aria-live="polite"
        >
          <div className="reports-embed-overlay-inner">
            {loadingImage ? (
              <img
                src={loadingImage}
                alt="Loading report…"
                className="reports-embed-overlay-image"
              />
            ) : (
              <>
                <div className="reports-embed-overlay-orb" />
                <div className="reports-embed-overlay-copy">Loading report…</div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
